import CheckRole from "./CheckRole";
import { useEffect, useState } from "react";
import Unauthorized from "../roles/Unauthorized";
import { useSetUser, useUser } from "./UserContext";
import LoadingScreen from "../components/global/LoadingScreen";
import InternalServerError from "../components/global/InternalServerError";
import { useUserSettings } from "../components/context/UserSettingsContext";
import { useLazyGetUserByEmailV1Query, useLazyGetUserRolesV1Query, useLazyGetUserSettingsQuery, useLazyGetUserProtocolsV1Query } from "../store/penpalServerAPI";
import AcceptEULA from "../pages/AcceptEULA";

const CheckAuthorized = ({ email = "" }) => {
  const user = useUser();
  const updateUser = useSetUser();
  const [myRoles, setMyRoles] = useState([]);
  const { rolePref, setRolePref, protocolPref, setProtocolPref } = useUserSettings();
  const [authLoading, setAuthLoading] = useState(true);

  const [getUserByEmail, { data, isError, isSuccess, error, isLoading }] =
    useLazyGetUserByEmailV1Query();

  const [getUserProtocols, {
    data: protocolsData,
    isSuccess: protocolsIsSuccess,
    isError: protocolsIsError,
    error: protocolsError,
  }] = useLazyGetUserProtocolsV1Query()

  // Set protocolPref if it's not already set
  useEffect(() => {
    if (protocolsIsSuccess && protocolsData?.length > 0 && !protocolPref) {
      setProtocolPref(protocolsData[0].protocolId); // Adjust this if the protocol data structure differs
    }
  }, [protocolsIsSuccess, protocolsData, protocolPref]);

  const [ getUserSettings, {
    data: userSettingsData,
    isLoading: userSettingsIsLoading,
    isSuccess: userSettingsIsSuccess,
    isError: userSettingsIsError,
    error: userSettingsError
  }] = useLazyGetUserSettingsQuery();

  useEffect(() => {
    if (isError) {
      console.log("Error while fetching GET UserByEmail: ", error);
    }
  }, [isError, error]);

  const [
    getUserRoles,
    {
      data: rolesData,
      isError: rolesIsError,
      error: rolesError,
      isSuccess: rolesIsSuccess,
      isLoading: rolesIsLoading,
    },
  ] = useLazyGetUserRolesV1Query();

  useEffect(() => {
    if (rolesIsError) {
      console.log("Error while fetching GET UserRoles: ", rolesError);
    }
  }, [rolesIsError, rolesError]);

  // Fetch user data by email
  useEffect(() => {
    if (email.length) {
      getUserByEmail(email);
    }
  }, [email]);

  // Fetch user roles based on user data
  useEffect(() => {
    if (isSuccess && Object.keys(data).length > 0) {
      getUserSettings(data.userId);
      getUserProtocols(data.userId);
      getUserRoles(data.userId);
    }
  }, [data, isSuccess]);

  // Process user roles data
  useEffect(() => {
    if (rolesIsSuccess) {
      if (rolesData?.length > 0) {
        const newRoles = rolesData.map((obj) => obj.roleName);
        setMyRoles(newRoles);
      } else {
        setMyRoles(["NONE"]);
      }
  
      setAuthLoading(false);
    } else if (rolesIsError) {
      console.error("ERROR: Could not retrieve User Roles.");
      setAuthLoading(false);
    }
  }, [rolesData, rolesIsSuccess, rolesIsError]);
  

  // Update user context with authorization and role data
  useEffect(() => {
    if (rolesIsSuccess && data && userSettingsIsSuccess && myRoles.length > 0) { 
      const myRole =
        userSettingsData && myRoles.includes(rolePref)
          ? rolePref
          : myRoles[0] === "NONE"
            ? "NONE"
            : myRoles[0];
  
      if (!userSettingsData && myRoles[0] !== "NONE") {
        setRolePref(myRoles[0]);
      }
  
      updateUser({
        UserID: data.userId,
        Email: data.email,
        Phone: data.phone,
        FirstName: data.firstName,
        LastName: data.lastName,
        SelectedRole: myRole,
        Roles: myRoles,
        Authorized: data.authorized,
        DateTimeCreated: data.dateTimeCreated,
        AcceptedEULA: data.acceptedEULA
      });
    }
  }, [rolesIsSuccess, data, rolePref, userSettingsIsSuccess, myRoles]); 

  if (error?.status === 500 || rolesError?.status === 500)
    return <InternalServerError />;
  if (isError || rolesError) return <Unauthorized />;
  if (authLoading || isLoading || rolesIsLoading)
    return <LoadingScreen screenHeight={true} />;
  if (!user.AcceptedEULA) return <AcceptEULA user={user}/>; 
  else if (user.AcceptedEULA && user.Authorized) return <CheckRole/>;
};

export default CheckAuthorized;
